import useAxios from "../../utils/useAxios";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  ListItem,
  ListItemButton,
  Typography,
} from "@mui/material";
import { Dropdown } from "antd";
import { Edit, MoreVert } from "@mui/icons-material";
import { DocsModal } from "../Documents/DocumentModal";
import { useContentType } from "../../utils/contentTypes";
import { useAccessControl } from "../../hooks/useAccessControl";
import { buildContextMenu } from "../Menu/DynamicMenu";
import AssetCardProgressTracker from "../../features/Logistics/components/progress/AssetCardProgressTracker";
import { isTrackedLogisticsAsset } from "../../features/Logistics/models/logisticsProgressPayload";
import useLongPress from "../../features/Logistics/hooks/useLongPress";
import LogisticsNextAction from "../../features/Logistics/components/progress/LogisticsNextAction";
import { useLogisticsUpdateLauncher } from "../../features/Logistics/context/logisticsUpdateLauncherContext";
import { FAULT_COLORS } from "../../utils/faults";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useBreadcrumbs } from "../../context/BreadcrumbContext";

const PanelComponentCard = ({
  component,
  componentId,
  handleDeleteClick,
  handleInfoClick,
  handleEditComponent,
  selectedComponent,
  setSelectedComponent,
  viewMode,
  // ── minimal (side-panel) variant ──
  variant = "default",   // "default" | "minimal"
  isSelected = false,
  onSelect,              // (component) => void — called when the list item is clicked
  setSelectedTier,       // needed to clear tier selection on item click (minimal only)
  logisticsVariant = "default",
  showLogisticsDetails = false,
  logisticsActionsSlot = null,
  onLogisticsSelect,
  onLogisticsLongPress = null,
  logisticsIsChecked = false,
}) => {
  const api = useAxios();
  const navigate = useNavigate();
  const { wasLongPress, ...longPressHandlers } = useLongPress(onLogisticsLongPress);

  const { setComponent, breadcrumbs, selectedItems } =
      useBreadcrumbs();

  const { siteId, plantId, assetId, subAssetId } = useParams();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [permissionsRequested, setPermissionsRequested] = useState(false);
  const { contentTypeId, loading: contentTypeLoading } = useContentType(
    api,
    "panelComponent",
  );

  const componentSeverity = component.fault?.severity || "healthy";
  const ribbonColor = FAULT_COLORS[componentSeverity];
  const isLogisticsCard = logisticsVariant === "logistics";
  const shouldShowLogisticsDetails =
    isLogisticsCard && showLogisticsDetails;
  const wrappedLogisticsActionsSlot = logisticsActionsSlot ? (
    <Box
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
      sx={{
        width: 32,
        height: 24,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        mt: "1px",
      }}
    >
      {logisticsActionsSlot}
    </Box>
  ) : null;
  const shouldUseLogisticsTrackerLayout =
    isLogisticsCard && Boolean(logisticsActionsSlot);

  const shouldLoadPermissions =
    permissionsRequested ||
    dropdownOpen ||
    (variant !== "minimal" && viewMode !== "grid");

  const { canEdit, canDelete, canShare } = useAccessControl(
    "panelcomponent",
    component.id,
    { enabled: shouldLoadPermissions },
  );

  const requestPermissions = () => setPermissionsRequested(true);

  const handleDropdownOpenChange = (nextOpen) => {
    if (nextOpen) requestPermissions();
    setDropdownOpen(nextOpen);
  };

  const related = component.relatedTo || [];
  const networkAssets = related.filter((eq) => eq.tagNumber?.includes("AS"));
  const powerAssets = related.filter((eq) => eq.tagNumber?.includes("MCP"));
  // tier_details comes from browsing the hierarchy (nested serializer);
  // tierTagNumber/tierName come from search results, which don't include
  // nested detail objects — fall back to whichever is available.
  const parentTierLabel =
    component.tier_details?.tagNumber ||
    component.tierTagNumber ||
    component.tierName ||
    null;

  const handleCardClick = () => {
    setSelectedComponent(component);
    setComponent({ name: component.tagNumber, id: component.id });
    navigate(
      `/sites/${siteId}/plants/${plantId}/assets/${assetId}/sub-asset/${subAssetId}/components/${component.id}`,
    );
  };

  const handlePrimaryCardClick = () => {
    if (wasLongPress()) return;

    if (onLogisticsSelect) {
      onLogisticsSelect(component);
      return;
    }

    handleCardClick();
  };

  const logisticsUpdate = useLogisticsUpdateLauncher();

  const menuItems = buildContextMenu({
    type: "component",
    object: component,
    objectId: component.id,
    name: component.tagNumber,

    canEdit,
    canDelete,
    canShare,

    contentTypeId,
    contentTypeLoading,

    onInfo: handleInfoClick,
    onUpdateLogistics: logisticsUpdate.canUpdate(component) ? logisticsUpdate.open : undefined,
    onEdit: handleEditComponent,
    onDelete: handleDeleteClick,

    DocsComponent: (
      <div
        onClick={(e) => {
          e.stopPropagation();
          setDropdownOpen(false);
        }}
      >
        <DocsModal objectType="component" object={component} />
      </div>
    ),
  });

  const sharedCardSx = {
    border:
      selectedComponent?.id === componentId
        ? "2px solid #1976d2"
        : "1px solid #e0e0e0",
    backgroundColor:
      selectedComponent?.id === componentId ? "#e3f2fd" : "background.paper",
    boxShadow:
      selectedComponent?.id === componentId
        ? "0 6px 20px rgba(25, 118, 210, 0.25)"
        : "0 3px 6px rgba(0,0,0,0.1)",
    // Extra feedback for a long-press-selected card on touch, alongside the
    // checkbox (which is visible there too, not hidden).
    ...(logisticsIsChecked && {
      "@media (pointer: coarse)": {
        border: "2px solid #1976d2",
        backgroundColor: "#e3f2fd",
        boxShadow: "0 6px 20px rgba(25, 118, 210, 0.25)",
      },
    }),
    // Without this, holding a finger on the card for ~500ms lets the browser
    // start its own native long-press gesture first (text selection on
    // Android, the callout menu on iOS) — that fires pointercancel and
    // clears useLongPress's timer before it reaches threshold, so our
    // custom long-press never fires on a real touch device even though it
    // works in tests. touchAction stays "manipulation" (not "none") so
    // vertical list scrolling that starts on a card still works.
    ...(onLogisticsLongPress && {
      userSelect: "none",
      WebkitUserSelect: "none",
      WebkitTouchCallout: "none",
      touchAction: "manipulation",
    }),
  };

  // ── Minimal variant: side-panel list item ────────────────────────────────
  if (variant === "minimal") {
    return (
      <ListItem disablePadding>
        <ListItemButton
          sx={{
            borderRadius: 1,
            mb: 1,
            position: "relative",
            flexDirection: "column",
            alignItems: "flex-start",
            transition: "background-color 0.3s ease, color 0.3s ease",
            padding: 2,
            pt: 1.25,
            borderLeft: `3px solid ${ribbonColor}`,
            borderRadius: "0 8px 8px 0",
            bgcolor: isSelected ? "primary.light" : "transparent",
            color: isSelected ? "primary.contrastText" : "inherit",
            "&:hover": {
              bgcolor: "primary.light",
              color: "primary.contrastText",
            },
          }}
          onClick={() => {
            onSelect?.(component);
            setSelectedTier?.(null);
          }}
        >
          <AssetCardProgressTracker
            item={component}
            density="compact"
            sx={{ width: "calc(100% - 40px)", mb: 1 }}
          />

          <Typography
            sx={{ fontSize: "0.8rem", fontWeight: 700, mb: 0.25 }}
            noWrap
          >
            {component.tagNumber}
          </Typography>

          <Typography
            sx={{
              fontSize: "0.75rem",
              color: isSelected ? "primary.contrastText" : "text.secondary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
          >
            {component.componentName}
          </Typography>

          {component.instrumentType && (
            <Typography
              sx={{
                fontSize: "0.7rem",
                color: isSelected
                  ? "primary.contrastText"
                  : "text.disabled",
                mt: 0.25,
              }}
            >
              {component.instrumentType}
            </Typography>
          )}

          <Dropdown
            menu={{ items: menuItems }}
            trigger={["click"]}
            open={dropdownOpen}
            onOpenChange={handleDropdownOpenChange}
          >
            <IconButton
              aria-label="settings"
              onClick={(e) => {
                e.stopPropagation();
                requestPermissions();
              }}
              onMouseEnter={requestPermissions}
              onFocus={requestPermissions}
              size="small"
              sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </Dropdown>
        </ListItemButton>
      </ListItem>
    );
  }

  // ── Default grid variant ─────────────────────────────────────────────────
  return viewMode === "grid" ? (
    <Grid
      item
      xs={12}
      sm={isLogisticsCard ? 6 : 3}
      md={4}
      lg={isLogisticsCard ? 3 : 2.5}
    >
      <Card
        onClick={handlePrimaryCardClick}
        onContextMenu={(event) => {
          if (onLogisticsLongPress) event.preventDefault();
        }}
        {...longPressHandlers}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          borderRadius: "20px",
          position: "relative",
          cursor: "pointer",
          transition: "all 0.25s ease",
          bgcolor: "background.paper",
          overflow: "hidden",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          "&:hover": {
            boxShadow: "0 12px 40px rgba(0,0,0,0.14)",
            transform: "translateY(-4px)",
            borderColor: "primary.main",
          },
          ...sharedCardSx,
        }}
      >
        {/* ── Top accent bar ── */}
        <Box sx={{ height: 6, bgcolor: ribbonColor, width: "100%" }} />
        <Box
          sx={
            shouldUseLogisticsTrackerLayout
              ? {
                  display: "grid",
                  gridTemplateColumns: "32px minmax(0, 1fr) 32px",
                  alignItems: "flex-start",
                  columnGap: 1,
                  pt: 1.25,
                  px: 1.5,
                  pb: 0.5,
                }
              : {
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  pt: 1.25,
                  // The real tracker's rightmost node needs pr:7 clearance
                  // from the floating ⋮ menu; an untracked item's invisible
                  // tracker doesn't, so it stays centered on the card by
                  // using symmetric padding instead.
                  pr: isTrackedLogisticsAsset(component) ? 7 : 2,
                  pb: 0.5,
                  pl: 2,
                }
          }
        >
          {shouldUseLogisticsTrackerLayout ? (
            <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
              {wrappedLogisticsActionsSlot}
            </Box>
          ) : (
            wrappedLogisticsActionsSlot
          )}
          <Box
            sx={
              shouldUseLogisticsTrackerLayout
                ? { minWidth: 0 }
                : { flex: 1, minWidth: 0 }
            }
          >
            <AssetCardProgressTracker item={component} />
          </Box>
          {shouldUseLogisticsTrackerLayout && <Box aria-hidden="true" />}
        </Box>

        {/* ── Card Header ── */}
        <CardHeader
          action={
            <Dropdown
              menu={{ items: menuItems }}
              trigger={["click"]}
              open={dropdownOpen}
              onOpenChange={handleDropdownOpenChange}
              onClick={(e) => e.stopPropagation()}
            >
              <IconButton
                aria-label="settings"
                onClick={(e) => {
                  e.stopPropagation();
                  requestPermissions();
                }}
                onMouseEnter={requestPermissions}
                onFocus={requestPermissions}
                size="small"
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 8,
                  zIndex: 1,
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <MoreVert fontSize="small" />
              </IconButton>
            </Dropdown>
          }
          title={
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: "primary.main",
                textAlign: "center",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: { xs: "0.85rem", sm: "0.9rem", md: "1rem" },
              }}
              noWrap
            >
              {component.tagNumber}
            </Typography>
          }
          sx={{
            pt: 0.75,
            pb: 0,
            px: 2.5,
            "& .MuiCardHeader-content": {
              overflow: "hidden",
              textAlign: "center",
            },
          }}
        />

        {/* ── Card Content ── */}
        <CardContent sx={{ pt: 1.5, px: 2.5, pb: 2 }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" },
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              color: "text.secondary",
              textAlign: "center",
            }}
          >
            {component.description}
          </Typography>

          <Divider sx={{ my: 0.5, opacity: 0.6 }} />

          {/* Metadata */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
            {shouldShowLogisticsDetails && parentTierLabel && (
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontWeight: 500 }}
                >
                  Parent
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {parentTierLabel}
                </Typography>
              </Box>
            )}

            {networkAssets.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontWeight: 500 }}
                >
                  PLC
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {networkAssets.map((e) => e.tagNumber).join(", ")}
                </Typography>
              </Box>
            )}

            {powerAssets.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontWeight: 500 }}
                >
                  MCP
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {powerAssets.map((e) => e.tagNumber).join(", ")}
                </Typography>
              </Box>
            )}
          </Box>
          <LogisticsNextAction item={component} sx={{ mt: 1 }} />
        </CardContent>
      </Card>
    </Grid>
  ) : (
    // ── List view ────────────────────────────────────────────────────────────
    <Box
      sx={{
        width: "100%",
        display: "flex",
        borderLeft: `4px solid ${ribbonColor}`,
      }}
    >
      <Card
        data-active={selectedComponent?.id === component.id ? "" : undefined}
        onClick={handlePrimaryCardClick}
        sx={{
          height: "100%",
          width: "100%",
          cursor: "pointer",
          position: "relative",
          overflow: "hidden",
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "scale(1.01)",
            boxShadow: "0 6px 18px rgba(0,0,0,0.15)",
          },
          ...sharedCardSx,
        }}
      >
        <CardContent
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            pt: 2,
          }}
        >
          <Box sx={{ flex: "0 0 170px", minWidth: 0 }}>
            <AssetCardProgressTracker item={component} density="compact" />
            {shouldShowLogisticsDetails && parentTierLabel && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  fontWeight: 600,
                  color: "text.secondary",
                  mt: 0.5,
                }}
                noWrap
              >
                Parent: {parentTierLabel}
              </Typography>
            )}
            <LogisticsNextAction
              item={component}
              compact
              sx={{ mt: 0.5 }}
            />
          </Box>
          <Box sx={{ flex: "0 0 12%", minWidth: "120px" }}>
            <Typography variant="subtitle2" color="text.secondary">
              Tag Number
            </Typography>
            <Typography variant="body2" noWrap color="primary.main">
              {component.tagNumber}
            </Typography>
          </Box>
          <Box sx={{ flex: "0 0 12%", minWidth: "120px" }}>
            <Typography variant="subtitle2" color="text.secondary">
              Name
            </Typography>
            <Typography variant="body2" noWrap>
              {component.componentName}
            </Typography>
          </Box>
          <Box sx={{ flex: "1", minWidth: "150px" }}>
            <Typography variant="subtitle2" color="text.secondary">
              Description
            </Typography>
            <Typography
              variant="body2"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {component.description}
            </Typography>
          </Box>

          {canEdit && (
            <Typography
              onClick={(e) => {
                e.stopPropagation();
                handleEditComponent(component.id);
              }}
              sx={{
                color: "success.main",
                cursor: "pointer",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Edit fontSize="small" /> EDIT
            </Typography>
          )}

          <Divider orientation="vertical" flexItem sx={{ marginX: 1.5 }} />

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Dropdown
              menu={{ items: menuItems }}
              trigger={["click"]}
              open={dropdownOpen}
              onOpenChange={handleDropdownOpenChange}
            >
              <IconButton
                aria-label="settings"
                onClick={(e) => {
                  e.stopPropagation();
                  requestPermissions();
                }}
                onMouseEnter={requestPermissions}
                onFocus={requestPermissions}
              >
                <MoreVert />
              </IconButton>
            </Dropdown>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PanelComponentCard;
